import React, { useState, useEffect, useCallback, memo } from "react";
import { useAuth, API } from "../App";
import { useBudgetStore } from "../store";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Wallet,
  Plane,
  Car,
  Fuel,
  Hotel,
  MapPin,
  Train,
  Utensils,
  Plus,
  Trash2,
  Pencil,
  Loader2,
  TrendingUp,
  PieChart,
  StickyNote,
  FileText,
  X,
  Camera,
  Image,
  Users,
  DollarSign,
  Check,
  UserPlus,
  Minus,
} from "lucide-react";
import { CurrencyConverter } from "../components/CurrencyConverter";

const BUDGET_CATEGORIES = [
  { id: "flights", name: "Flights", icon: Plane, color: "from-sky-500 to-blue-600" },
  { id: "car_rental", name: "Car Rental", icon: Car, color: "from-indigo-500 to-purple-600" },
  { id: "car_fuel", name: "Car Fuel", icon: Fuel, color: "from-amber-500 to-orange-600" },
  { id: "hotels", name: "Hotels", icon: Hotel, color: "from-rose-500 to-pink-600" },
  { id: "sightseeings", name: "Sightseeings", icon: MapPin, color: "from-emerald-500 to-teal-600" },
  { id: "trains_buses", name: "Train & Buses", icon: Train, color: "from-lime-500 to-green-600" },
  { id: "restaurants", name: "Restaurants", icon: Utensils, color: "from-orange-500 to-red-600" },
];

const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "JPY", "AUD", "CAD", "SEK", "NOK", "DKK"];

// Memoized Expense Form
const ExpenseForm = memo(({ expense, setExpense, onSubmit, isEdit = false, saving }) => (
  <div className="space-y-4 mt-4">
    <div>
      <Label className="text-zinc-400">Category *</Label>
      <Select
        value={expense.category}
        onValueChange={(value) => setExpense({ ...expense, category: value })}
      >
        <SelectTrigger className="glass-input">
          <SelectValue placeholder="Select category" />
        </SelectTrigger>
        <SelectContent className="glass-card border-white/10">
          {BUDGET_CATEGORIES.map((cat) => (
            <SelectItem key={cat.id} value={cat.id}>
              <div className="flex items-center gap-2">
                <cat.icon className="w-4 h-4" />
                {cat.name}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div>
      <Label className="text-zinc-400">Description</Label>
      <Input
        className="glass-input"
        placeholder="e.g., Flight to Paris, Hotel booking"
        value={expense.description}
        onChange={(e) => setExpense({ ...expense, description: e.target.value })}
        autoComplete="off"
      />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-zinc-400">Amount *</Label>
        <Input
          type="number"
          step="0.01"
          className="glass-input"
          placeholder=""
          value={expense.amount}
          onChange={(e) => setExpense({ ...expense, amount: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-zinc-400">Currency</Label>
        <Select
          value={expense.currency}
          onValueChange={(value) => setExpense({ ...expense, currency: value })}
        >
          <SelectTrigger className="glass-input">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="glass-card border-white/10">
            {CURRENCIES.map((curr) => (
              <SelectItem key={curr} value={curr}>{curr}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>

    <div>
      <Label className="text-zinc-400">Date</Label>
      <Input
        type="date"
        className="glass-input"
        value={expense.date}
        onChange={(e) => setExpense({ ...expense, date: e.target.value })}
      />
    </div>

    <Button
      className="w-full btn-primary"
      onClick={onSubmit}
      disabled={saving}
      data-testid={isEdit ? "update-expense-btn" : "save-expense-btn"}
    >
      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
      {isEdit ? "Update Expense" : "Add Expense"}
    </Button>
  </div>
));

ExpenseForm.displayName = "ExpenseForm";

// Memoized Note Form with Photo Upload and Split Bill
const NoteForm = memo(({ note, setNote, onSubmit, isEdit = false, saving, onPhotoUpload, uploadingPhoto }) => {
  const fileInputRef = React.useRef(null);
  const [showSplitBill, setShowSplitBill] = React.useState(!!note.split_bill);
  
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file && onPhotoUpload) {
      await onPhotoUpload(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (indexToRemove) => {
    const newPhotos = (note.photos || []).filter((_, index) => index !== indexToRemove);
    setNote({ ...note, photos: newPhotos });
  };

  // Split Bill handlers
  const initializeSplitBill = () => {
    setShowSplitBill(true);
    setNote({
      ...note,
      split_bill: {
        total_amount: 0,
        num_people: 2,
        amount_per_person: 0,
        people: [
          { name: "Person 1", amount: 0, paid: false },
          { name: "Person 2", amount: 0, paid: false }
        ]
      }
    });
  };

  const updateSplitBillTotal = (totalAmount) => {
    const splitBill = note.split_bill || { total_amount: 0, num_people: 2, people: [] };
    const numPeople = splitBill.people?.length || splitBill.num_people || 2;
    const amountPerPerson = totalAmount / numPeople;
    
    const updatedPeople = (splitBill.people || []).map(p => ({
      ...p,
      amount: amountPerPerson
    }));
    
    setNote({
      ...note,
      split_bill: {
        ...splitBill,
        total_amount: totalAmount,
        amount_per_person: amountPerPerson,
        people: updatedPeople
      }
    });
  };

  const updateNumPeople = (newNum) => {
    if (newNum < 1) return;
    const splitBill = note.split_bill || { total_amount: 0 };
    const currentPeople = splitBill.people || [];
    const amountPerPerson = splitBill.total_amount / newNum;
    
    let updatedPeople = [...currentPeople];
    
    if (newNum > currentPeople.length) {
      // Add new people
      for (let i = currentPeople.length; i < newNum; i++) {
        updatedPeople.push({ name: `Person ${i + 1}`, amount: amountPerPerson, paid: false });
      }
    } else if (newNum < currentPeople.length) {
      // Remove people
      updatedPeople = updatedPeople.slice(0, newNum);
    }
    
    // Update amounts for all
    updatedPeople = updatedPeople.map(p => ({ ...p, amount: amountPerPerson }));
    
    setNote({
      ...note,
      split_bill: {
        ...splitBill,
        num_people: newNum,
        amount_per_person: amountPerPerson,
        people: updatedPeople
      }
    });
  };

  const updatePersonName = (index, name) => {
    const splitBill = note.split_bill;
    const updatedPeople = [...splitBill.people];
    updatedPeople[index] = { ...updatedPeople[index], name };
    setNote({
      ...note,
      split_bill: { ...splitBill, people: updatedPeople }
    });
  };

  const togglePersonPaid = (index) => {
    const splitBill = note.split_bill;
    const updatedPeople = [...splitBill.people];
    updatedPeople[index] = { ...updatedPeople[index], paid: !updatedPeople[index].paid };
    setNote({
      ...note,
      split_bill: { ...splitBill, people: updatedPeople }
    });
  };

  const removeSplitBill = () => {
    setShowSplitBill(false);
    setNote({ ...note, split_bill: null });
  };

  return (
    <div className="space-y-4 mt-4">
      <div>
        <Label className="text-zinc-400">Title *</Label>
        <Input
          className="glass-input"
          placeholder="e.g., Restaurant bill, Trip reminders"
          value={note.title}
          onChange={(e) => setNote({ ...note, title: e.target.value })}
          autoComplete="off"
          data-testid="note-title-input"
        />
      </div>

      <div>
        <Label className="text-zinc-400">Content *</Label>
        <Textarea
          className="glass-input min-h-[100px]"
          placeholder="Write your note here... e.g., Split dinner bill with friends"
          value={note.content}
          onChange={(e) => setNote({ ...note, content: e.target.value })}
          data-testid="note-content-input"
        />
      </div>

      {/* Split Bill Section */}
      <div className="border border-slate-700/50 rounded-lg p-3 bg-slate-800/30">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-zinc-400 flex items-center gap-2">
            <Users className="w-4 h-4 text-green-400" />
            Split Bill
          </Label>
          {!showSplitBill ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="btn-outline text-xs"
              onClick={initializeSplitBill}
              data-testid="enable-split-bill-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Split
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-red-400 hover:text-red-300 text-xs"
              onClick={removeSplitBill}
            >
              <X className="w-3 h-3 mr-1" />
              Remove
            </Button>
          )}
        </div>
        
        {showSplitBill && note.split_bill && (
          <div className="space-y-3">
            {/* Total Amount */}
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-400" />
              <Input
                type="number"
                className="glass-input flex-1"
                placeholder="Total amount"
                value={note.split_bill.total_amount || ""}
                onChange={(e) => updateSplitBillTotal(parseFloat(e.target.value) || 0)}
                data-testid="split-bill-total"
              />
            </div>
            
            {/* Number of People */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">Number of people:</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-8 h-8 p-0"
                  onClick={() => updateNumPeople((note.split_bill.people?.length || 2) - 1)}
                  disabled={(note.split_bill.people?.length || 2) <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-white font-semibold w-6 text-center">
                  {note.split_bill.people?.length || 2}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-8 h-8 p-0"
                  onClick={() => updateNumPeople((note.split_bill.people?.length || 2) + 1)}
                  data-testid="add-person-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Amount Per Person */}
            <div className="text-center py-2 bg-green-500/10 rounded-lg border border-green-500/20">
              <span className="text-sm text-zinc-400">Each person pays:</span>
              <div className="text-lg font-bold text-green-400">
                ${(note.split_bill.amount_per_person || 0).toFixed(2)}
              </div>
            </div>
            
            {/* People List */}
            <div className="space-y-2">
              <Label className="text-zinc-500 text-xs">Track who paid:</Label>
              {note.split_bill.people?.map((person, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    className="glass-input flex-1 text-sm"
                    placeholder={`Person ${index + 1}`}
                    value={person.name}
                    onChange={(e) => updatePersonName(index, e.target.value)}
                    data-testid={`person-name-${index}`}
                  />
                  <span className="text-sm text-zinc-400 w-16 text-right">
                    ${person.amount?.toFixed(2)}
                  </span>
                  <Button
                    type="button"
                    variant={person.paid ? "default" : "outline"}
                    size="sm"
                    className={`w-20 ${person.paid ? "bg-green-600 hover:bg-green-700" : ""}`}
                    onClick={() => togglePersonPaid(index)}
                    data-testid={`toggle-paid-${index}`}
                  >
                    {person.paid ? (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Paid
                      </>
                    ) : (
                      "Unpaid"
                    )}
                  </Button>
                </div>
              ))}
            </div>
            
            {/* Summary */}
            {note.split_bill.people && (
              <div className="text-xs text-zinc-500 flex justify-between pt-2 border-t border-slate-700/50">
                <span>
                  Paid: {note.split_bill.people.filter(p => p.paid).length} / {note.split_bill.people.length}
                </span>
                <span>
                  Remaining: ${(note.split_bill.people.filter(p => !p.paid).reduce((sum, p) => sum + (p.amount || 0), 0)).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Photo Upload Section */}
      <div>
        <Label className="text-zinc-400 flex items-center gap-2">
          <Camera className="w-4 h-4" />
          Photos (e.g., receipts, bills)
        </Label>
        
        {/* Photo Preview Grid */}
        {note.photos && note.photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-2 mb-2">
            {note.photos.map((photo, index) => (
              <div key={index} className="relative group aspect-square">
                <img 
                  src={photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg border border-slate-700"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="photo-upload-input"
        />
        <Button
          type="button"
          variant="outline"
          className="w-full btn-outline mt-2"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingPhoto}
          data-testid="add-photo-btn"
        >
          {uploadingPhoto ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Image className="w-4 h-4 mr-2" />
          )}
          {uploadingPhoto ? "Uploading..." : "Add Photo"}
        </Button>
      </div>

      <div>
        <Label className="text-zinc-400">Category (Optional)</Label>
        <Select
          value={note.category || "none"}
          onValueChange={(value) => setNote({ ...note, category: value === "none" ? null : value })}
        >
          <SelectTrigger className="glass-input">
            <SelectValue placeholder="Link to a category..." />
          </SelectTrigger>
          <SelectContent className="glass-card border-white/10">
            <SelectItem value="none">No category</SelectItem>
            {BUDGET_CATEGORIES.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                <div className="flex items-center gap-2">
                  <cat.icon className="w-4 h-4" />
                  {cat.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        className="w-full btn-primary"
        onClick={onSubmit}
        disabled={saving}
        data-testid={isEdit ? "update-note-btn" : "save-note-btn"}
      >
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        {isEdit ? "Update Note" : "Add Note"}
      </Button>
    </div>
  );
});

NoteForm.displayName = "NoteForm";

const MyBudget = () => {
  const { user } = useAuth();
  // Use Zustand store for expenses and notes
  const { 
    expenses, 
    notes, 
    setExpenses, 
    setNotes, 
    isLoading: storeLoading,
    setLoading: setStoreLoading,
    totalByCategory,
    totalByCurrency 
  } = useBudgetStore();
  
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddNoteDialog, setShowAddNoteDialog] = useState(false);
  const [showEditNoteDialog, setShowEditNoteDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [defaultCurrency, setDefaultCurrency] = useState("EUR");
  const [activeTab, setActiveTab] = useState("expenses");
  const [plannedExpenses, setPlannedExpenses] = useState(0);
  const [isEditingPlanned, setIsEditingPlanned] = useState(false);
  const [plannedInput, setPlannedInput] = useState("");

  const emptyExpense = {
    category: "",
    description: "",
    amount: "",
    currency: "EUR",
    date: new Date().toISOString().split("T")[0],
  };

  const emptyNote = {
    title: "",
    content: "",
    category: null,
    photos: [],
    split_bill: null,
  };

  const [newExpense, setNewExpense] = useState(emptyExpense);
  const [editExpense, setEditExpense] = useState(emptyExpense);
  const [newNote, setNewNote] = useState(emptyNote);
  const [editNote, setEditNote] = useState(emptyNote);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    const loadBudgetData = async () => {
      try {
        const [expensesRes, notesRes] = await Promise.all([
          axios.get(`${API}/budget/expenses`, { withCredentials: true }),
          axios.get(`${API}/budget/notes`, { withCredentials: true }),
        ]);
        setExpenses(expensesRes.data);
        setNotes(notesRes.data);
      } catch (error) {
        setExpenses([]);
        setNotes([]);
      } finally {
        setLoading(false);
      }
    };
    loadBudgetData();
    const saved = localStorage.getItem(`plannedExpenses_${user?.user_id}`);
    if (saved) {
      setPlannedExpenses(parseFloat(saved) || 0);
    }
  }, [user?.user_id]);

  // Expense handlers
  const handleAddExpense = async () => {
    if (!newExpense.category || !newExpense.amount) {
      toast.error("Please select category and enter amount");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/budget/expenses`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount),
      }, {
        withCredentials: true,
      });
      setExpenses([response.data, ...expenses]);
      toast.success("Expense added successfully");
      setShowAddDialog(false);
      setNewExpense({ ...emptyExpense, currency: defaultCurrency });
    } catch (error) {
      toast.error("Failed to add expense");
    } finally {
      setSaving(false);
    }
  };

  const handleEditExpense = async () => {
    if (!editExpense.category || !editExpense.amount) {
      toast.error("Please select category and enter amount");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API}/budget/expenses/${selectedExpense.expense_id}`, {
        ...editExpense,
        amount: parseFloat(editExpense.amount),
      }, {
        withCredentials: true,
      });
      setExpenses(expenses.map(e => e.expense_id === selectedExpense.expense_id ? response.data : e));
      toast.success("Expense updated successfully");
      setShowEditDialog(false);
      setSelectedExpense(null);
    } catch (error) {
      toast.error("Failed to update expense");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) return;

    try {
      await axios.delete(`${API}/budget/expenses/${expenseId}`, {
        withCredentials: true,
      });
      setExpenses(expenses.filter(e => e.expense_id !== expenseId));
      toast.success("Expense deleted");
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  const openEditExpenseDialog = (expense) => {
    setSelectedExpense(expense);
    setEditExpense({
      category: expense.category || "",
      description: expense.description || "",
      amount: expense.amount?.toString() || "",
      currency: expense.currency || "EUR",
      date: expense.date || "",
    });
    setShowEditDialog(true);
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!newNote.title || !newNote.content) {
      toast.error("Please enter title and content");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/budget/notes`, newNote, {
        withCredentials: true,
      });
      setNotes([response.data, ...notes]);
      toast.success("Note added successfully");
      setShowAddNoteDialog(false);
      setNewNote(emptyNote);
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSaving(false);
    }
  };

  const handleEditNote = async () => {
    if (!editNote.title || !editNote.content) {
      toast.error("Please enter title and content");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API}/budget/notes/${selectedNote.note_id}`, editNote, {
        withCredentials: true,
      });
      setNotes(notes.map(n => n.note_id === selectedNote.note_id ? response.data : n));
      toast.success("Note updated successfully");
      setShowEditNoteDialog(false);
      setSelectedNote(null);
    } catch (error) {
      toast.error("Failed to update note");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;

    try {
      await axios.delete(`${API}/budget/notes/${noteId}`, {
        withCredentials: true,
      });
      setNotes(notes.filter(n => n.note_id !== noteId));
      toast.success("Note deleted");
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const openEditNoteDialog = (note) => {
    setSelectedNote(note);
    setEditNote({
      title: note.title || "",
      content: note.content || "",
      category: note.category || null,
      photos: note.photos || [],
      split_bill: note.split_bill || null,
    });
    setShowEditNoteDialog(true);
  };

  // Photo upload handler
  const handlePhotoUpload = async (file, isEdit = false) => {
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/budget/notes/upload-photo`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const photoUrl = response.data.photo_url;
      
      if (isEdit) {
        setEditNote(prev => ({
          ...prev,
          photos: [...(prev.photos || []), photoUrl]
        }));
      } else {
        setNewNote(prev => ({
          ...prev,
          photos: [...(prev.photos || []), photoUrl]
        }));
      }
      
      toast.success("Photo uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const openAddExpenseDialogWithCategory = (categoryId) => {
    setNewExpense({
      ...emptyExpense,
      category: categoryId,
      currency: defaultCurrency,
    });
    setShowAddDialog(true);
  };

  // Calculate totals by category
  const getCategoryTotal = (categoryId) => {
    return expenses
      .filter(e => e.category === categoryId)
      .reduce((sum, e) => sum + (e.amount || 0), 0);
  };

  const getTotalExpenses = () => {
    return expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  };

  const getCategoryExpenses = (categoryId) => {
    return expenses.filter(e => e.category === categoryId);
  };

  const getCategoryInfo = (categoryId) => {
    return BUDGET_CATEGORIES.find(c => c.id === categoryId) || BUDGET_CATEGORIES[0];
  };

  const handleSavePlannedExpenses = () => {
    const value = parseFloat(plannedInput) || 0;
    setPlannedExpenses(value);
    localStorage.setItem(`plannedExpenses_${user?.user_id}`, value.toString());
    setIsEditingPlanned(false);
    toast.success("Planned budget saved!");
  };

  const startEditingPlanned = () => {
    setPlannedInput(plannedExpenses.toString());
    setIsEditingPlanned(true);
  };

  const getRemainingBudget = () => {
    return plannedExpenses - getTotalExpenses();
  };

  const getBudgetPercentage = () => {
    if (plannedExpenses <= 0) return 0;
    return Math.min((getTotalExpenses() / plannedExpenses) * 100, 100);
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container pb-28" data-testid="my-budget-page">
      <SEO title="Travel Budget" description="Track travel expenses by category with a built-in currency converter for 170+ currencies." path="/budget" />
      <div className="max-w-4xl mx-auto">
        {/* Hero Banner */}
        <div className="relative h-36 sm:h-44 lg:h-52 overflow-hidden -mx-4 -mt-6 mb-6 rounded-b-2xl">
          <img src="https://images.unsplash.com/photo-1554672408-730436b60dde?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 pb-5">
            <h1 className="text-2xl font-bold font-heading text-white drop-shadow-lg">My Budget</h1>
            <p className="text-sm text-slate-300/80 mt-0.5 drop-shadow">Track your travel expenses</p>
          </div>
        </div>
        <div className="px-4">

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("expenses")}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === "expenses"
                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                : "bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:bg-slate-800"
            }`}
            data-testid="expenses-tab"
          >
            <PieChart className="w-4 h-4" />
            Expenses
          </button>
          <button
            onClick={() => setActiveTab("notes")}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === "notes"
                ? "bg-violet-500/20 text-violet-400 border border-violet-500/30"
                : "bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:bg-slate-800"
            }`}
            data-testid="notes-tab"
          >
            <StickyNote className="w-4 h-4" />
            Notes ({notes.length})
          </button>
        </div>

        {/* Currency Converter */}
        <div className="mb-6">
          <CurrencyConverter />
        </div>

        {activeTab === "expenses" ? (
          <>
            {/* Budget Summary */}
            <div className="glass-card p-4 mb-6">
              {/* Planned Expenses */}
              <div className="mb-4 pb-4 border-b border-slate-700/50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-zinc-400 flex items-center gap-1">
                      <TrendingUp className="w-3.5 h-3.5" />
                      Planned Budget
                    </p>
                    {isEditingPlanned ? (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-lg text-zinc-400">{defaultCurrency}</span>
                        <Input
                          type="number"
                          value={plannedInput}
                          onChange={(e) => setPlannedInput(e.target.value)}
                          className="glass-input w-32 text-xl font-bold"
                          placeholder="0.00"
                          autoFocus
                          onKeyDown={(e) => e.key === 'Enter' && handleSavePlannedExpenses()}
                          data-testid="planned-expenses-input"
                        />
                        <Button
                          size="sm"
                          className="btn-primary"
                          onClick={handleSavePlannedExpenses}
                          data-testid="save-planned-btn"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="btn-outline"
                          onClick={() => setIsEditingPlanned(false)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <button
                        onClick={startEditingPlanned}
                        className="text-2xl font-bold text-sky-400 hover:text-sky-300 transition-colors flex items-center gap-1"
                        data-testid="edit-planned-btn"
                      >
                        {plannedExpenses > 0 ? (
                          <>
                            {defaultCurrency} {plannedExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </>
                        ) : (
                          <span className="text-zinc-500 text-lg">Click to set budget</span>
                        )}
                        <Pencil className="w-3.5 h-3.5 ml-1 opacity-50" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Progress Bar */}
                {plannedExpenses > 0 && (
                  <div className="mt-3">
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          getBudgetPercentage() >= 100 
                            ? 'bg-red-500' 
                            : getBudgetPercentage() >= 80 
                              ? 'bg-amber-500' 
                              : 'bg-emerald-500'
                        }`}
                        style={{ width: `${getBudgetPercentage()}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-xs">
                      <span className="text-zinc-500">{getBudgetPercentage().toFixed(0)}% used</span>
                      <span className={getRemainingBudget() >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                        {getRemainingBudget() >= 0 ? 'Remaining: ' : 'Over budget: '}
                        {defaultCurrency} {Math.abs(getRemainingBudget()).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Total Expenses */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400">Total Expenses</p>
                  <p className="text-3xl font-bold text-white">
                    {defaultCurrency} {getTotalExpenses().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <Button
                  className="btn-primary"
                  onClick={() => {
                    setNewExpense({ ...emptyExpense, currency: defaultCurrency });
                    setShowAddDialog(true);
                  }}
                  data-testid="add-expense-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>

            {/* Category Cards */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {BUDGET_CATEGORIES.map((category) => {
                const Icon = category.icon;
                const total = getCategoryTotal(category.id);
                const count = getCategoryExpenses(category.id).length;

                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
                    className={`p-4 rounded-xl border transition-all ${
                      selectedCategory === category.id
                        ? "border-white/30 bg-white/5"
                        : "border-white/10 hover:border-white/20"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center mb-3`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-sm text-zinc-400 text-left">{category.name}</p>
                    <p className="text-lg font-semibold text-white text-left">
                      {defaultCurrency} {total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-zinc-500 text-left">{count} items</p>
                  </button>
                );
              })}
            </div>

            {/* Selected Category Expenses */}
            {selectedCategory && (
              <div className="glass-card p-4 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    {(() => {
                      const cat = getCategoryInfo(selectedCategory);
                      const Icon = cat.icon;
                      return <Icon className="w-5 h-5 text-emerald-400" />;
                    })()}
                    {getCategoryInfo(selectedCategory).name} Expenses
                  </h3>
                  <Button
                    size="sm"
                    variant="outline"
                    className="btn-outline"
                    onClick={() => openAddExpenseDialogWithCategory(selectedCategory)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>

                {getCategoryExpenses(selectedCategory).length === 0 ? (
                  <p className="text-zinc-500 text-sm text-center py-4">
                    No expenses in this category
                  </p>
                ) : (
                  <div className="space-y-2">
                    {getCategoryExpenses(selectedCategory).map((expense) => (
                      <div
                        key={expense.expense_id}
                        className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="text-white text-sm">{expense.description || "No description"}</p>
                          <p className="text-xs text-zinc-500">{expense.date}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-400 font-medium">
                            {expense.currency} {expense.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <button
                            onClick={() => openEditExpenseDialog(expense)}
                            className="p-1 hover:bg-sky-500/20 rounded transition-colors"
                          >
                            <Pencil className="w-4 h-4 text-sky-400" />
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense.expense_id)}
                            className="p-1 hover:bg-red-500/20 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recent Expenses */}
            <div className="glass-card p-4">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-violet-400" />
                Recent Expenses
              </h3>
              {expenses.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center py-8">
                  No expenses yet. Start tracking your travel budget!
                </p>
              ) : (
                <div className="space-y-2">
                  {expenses.slice(0, 10).map((expense) => {
                    const cat = getCategoryInfo(expense.category);
                    const Icon = cat.icon;

                    return (
                      <div
                        key={expense.expense_id}
                        className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg"
                      >
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${cat.color} flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{expense.description || cat.name}</p>
                          <p className="text-xs text-zinc-500">{expense.date}</p>
                        </div>
                        <span className="text-emerald-400 font-medium whitespace-nowrap">
                          {expense.currency} {expense.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Notes Tab */
          <>
            <div className="flex justify-end mb-4">
              <Button
                className="btn-primary"
                onClick={() => {
                  setNewNote(emptyNote);
                  setShowAddNoteDialog(true);
                }}
                data-testid="add-note-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Note
              </Button>
            </div>

            {notes.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-violet-500/20 flex items-center justify-center mx-auto mb-4">
                  <StickyNote className="w-8 h-8 text-violet-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">No notes yet</h3>
                <p className="text-zinc-400 text-sm max-w-sm mx-auto mb-4">
                  Add notes to keep track of budget tips, reminders, or any travel-related information.
                </p>
                <Button className="btn-primary" onClick={() => setShowAddNoteDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Note
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {notes.map((note) => {
                  const cat = note.category ? getCategoryInfo(note.category) : null;
                  
                  return (
                    <div
                      key={note.note_id}
                      className="glass-card p-4"
                      data-testid={`note-card-${note.note_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-violet-400" />
                          <h3 className="font-semibold text-white">{note.title}</h3>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEditNoteDialog(note)}
                            className="p-2 hover:bg-sky-500/20 rounded transition-colors"
                            data-testid={`edit-note-${note.note_id}`}
                          >
                            <Pencil className="w-4 h-4 text-sky-400" />
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.note_id)}
                            className="p-2 hover:bg-red-500/20 rounded transition-colors"
                            data-testid={`delete-note-${note.note_id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-zinc-300 text-sm whitespace-pre-wrap mb-3">
                        {note.content}
                      </p>

                      {/* Display photos if any */}
                      {note.photos && note.photos.length > 0 && (
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          {note.photos.map((photo, index) => (
                            <a
                              key={index}
                              href={photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="aspect-square"
                            >
                              <img 
                                src={photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo}
                                alt={`Photo ${index + 1}`}
                                className="w-full h-full object-cover rounded-lg border border-slate-700 hover:border-violet-500 transition-colors"
                              />
                            </a>
                          ))}
                        </div>
                      )}
                      
                      {/* Display Split Bill if any */}
                      {note.split_bill && note.split_bill.total_amount > 0 && (
                        <div className="bg-slate-800/50 rounded-lg p-3 mb-3 border border-slate-700/50">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-green-400" />
                            <span className="text-sm font-medium text-white">Split Bill</span>
                            <span className="text-xs text-zinc-400 ml-auto">
                              Total: ${note.split_bill.total_amount?.toFixed(2)}
                            </span>
                          </div>
                          <div className="text-center py-1 mb-2 bg-green-500/10 rounded border border-green-500/20">
                            <span className="text-sm text-green-400 font-semibold">
                              ${note.split_bill.amount_per_person?.toFixed(2)} per person
                            </span>
                          </div>
                          <div className="space-y-1">
                            {note.split_bill.people?.map((person, idx) => (
                              <div key={idx} className="flex items-center justify-between text-xs">
                                <span className="text-zinc-300">{person.name}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-zinc-400">${person.amount?.toFixed(2)}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                    person.paid 
                                      ? "bg-green-500/20 text-green-400" 
                                      : "bg-red-500/20 text-red-400"
                                  }`}>
                                    {person.paid ? "PAID" : "UNPAID"}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between text-xs mt-2 pt-2 border-t border-slate-700/50">
                            <span className="text-zinc-500">
                              {note.split_bill.people?.filter(p => p.paid).length} / {note.split_bill.people?.length} paid
                            </span>
                            <span className="text-orange-400">
                              Remaining: ${(note.split_bill.people?.filter(p => !p.paid).reduce((sum, p) => sum + (p.amount || 0), 0) || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        {cat ? (
                          <span className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${cat.color} text-white`}>
                            {cat.name}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-500">No category</span>
                        )}
                        <span className="text-xs text-zinc-500">
                          {new Date(note.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Expense Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="glass-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Add Expense</DialogTitle>
          </DialogHeader>
          <ExpenseForm 
            expense={newExpense} 
            setExpense={setNewExpense} 
            onSubmit={handleAddExpense}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="glass-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Expense</DialogTitle>
          </DialogHeader>
          <ExpenseForm 
            expense={editExpense} 
            setExpense={setEditExpense} 
            onSubmit={handleEditExpense} 
            isEdit
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={showAddNoteDialog} onOpenChange={setShowAddNoteDialog}>
        <DialogContent className="glass-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-violet-400" />
              Add Note
            </DialogTitle>
          </DialogHeader>
          <NoteForm 
            note={newNote} 
            setNote={setNewNote} 
            onSubmit={handleAddNote}
            saving={saving}
            onPhotoUpload={(file) => handlePhotoUpload(file, false)}
            uploadingPhoto={uploadingPhoto}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog open={showEditNoteDialog} onOpenChange={setShowEditNoteDialog}>
        <DialogContent className="glass-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-violet-400" />
              Edit Note
            </DialogTitle>
          </DialogHeader>
          <NoteForm 
            note={editNote} 
            setNote={setEditNote} 
            onSubmit={handleEditNote} 
            isEdit
            saving={saving}
            onPhotoUpload={(file) => handlePhotoUpload(file, true)}
            uploadingPhoto={uploadingPhoto}
          />
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default MyBudget;
